import { Sparkles, Loader2, Wand2, RefreshCw } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

/**
 * AI-branded "About" drafting field, reused across onboarding (user + company),
 * the admin Mentor form and the Venture editor. Carries the platform AI
 * signature: the `.ai-chip` badge, `.ai-text` gradient label and an
 * `.ai-ring` / `.ai-glow` generate affordance so AI surfaces stand out.
 *
 * AI auto-drafts on LinkedIn/company fetch (caller-driven); the buttons here
 * are for on-demand (re)generation when the user filled details manually.
 */
export const AiAboutField = ({
  label = 'About', value, onChange, busy, onGenerate, prompt, setPrompt,
  placeholder = 'Click Generate, or write your own…',
  taTid, genTid, promptTid, applyTid, rows = 3,
}) => (
  <div>
    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
      <Label className="text-xs flex items-center gap-2">
        <span className="ai-text font-medium">{label}</span>
        <span className="ai-chip" data-testid={genTid ? `${genTid}-badge` : undefined}>
          <Sparkles className="h-2.5 w-2.5" /> AI drafted
        </span>
      </Label>
      <Button
        type="button" size="sm"
        className="ai-ring h-7 gap-1.5 rounded-full bg-card hover:bg-accent/5 text-foreground px-3"
        onClick={() => onGenerate()} disabled={busy} data-testid={genTid}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" /> : <Sparkles className="h-3.5 w-3.5 text-accent" />}
        <span className="ai-text font-semibold text-xs">{value ? 'Regenerate' : 'Generate'}</span>
      </Button>
    </div>
    <Textarea
      value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
      placeholder={placeholder}
      className={value ? 'ai-glow border-accent/30 focus-visible:ring-accent/40' : ''}
      data-testid={taTid}
    />
    <div className="flex gap-2 mt-2">
      <div className="relative flex-1">
        <Sparkles className="h-3.5 w-3.5 text-accent absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder="Tell AI how to change it (e.g. make it punchier)"
          className="pl-9 h-9 text-sm" data-testid={promptTid}
          onKeyDown={(e) => { if (e.key === 'Enter' && prompt.trim() && !busy) { e.preventDefault(); onGenerate(); } }}
        />
      </div>
      <Button
        type="button" size="sm"
        className="ai-ring h-9 gap-1.5 shrink-0 rounded-full bg-card hover:bg-accent/5 text-foreground"
        onClick={() => onGenerate()} disabled={busy || !prompt.trim()} data-testid={applyTid}
      >
        <Wand2 className="h-3.5 w-3.5 text-accent" /> <span className="ai-text font-semibold">Apply</span>
      </Button>
    </div>
  </div>
);

export default AiAboutField;
